import React, { FunctionComponent, useMemo, useState } from 'react';
import Card from '../../../Card/Card';
import { Button, ToggleSwitch } from '../../../../design-system';
import { GearIcon } from '../../../../icons/Ui/GearIcon';
import { ChevronIcon } from '../../../../icons/Ui/ChevronIcon';
import { H5, H6 } from '../../../../design-system/Headings/Headings';
import Tooltip from '../../../../design-system/Tooltip/Tooltip';
import { AIChatbotProviderValue, useAIChatbot } from '../AIChatbotContext';

const WALKTHROUGH_VIDEO_URL = 'https://www.youtube.com/embed/0Qbe35W07Fc';

const STEPS = [
    {
        title: 'Activate the Context Plugins toggle',
        description:
            'Turn on the Context Plugins toggle so the widget appears in your Portal.',
    },
    {
        title: 'Configure SDK Publishing Settings',
        description:
            'Set up your package registry credentials and publishing configuration for each language.',
    },
    {
        title: 'Publish SDK Packages',
        description:
            'Push your SDK packages to NPM, PyPI, and other registries using the publishing wizard.',
    },
    {
        title: 'Add SDK Package Details to Your Portal',
        description:
            'Link your published packages to your portal so APIMatic can add SDK Package information to the AI Context.',
    },
    {
        title: 'Publish your Portal',
        description: 'Click "Publish Portal" in the top bar.',
    },
];


const ExternalLinkIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

interface SetupGuideCardProps {
    packagePublishingUrl: string;
}

export const SetupGuideCard: FunctionComponent<SetupGuideCardProps> = ({ packagePublishingUrl }) => {
    const [collapsed, setCollapsed] = useState(false);
    const { aiChatbotState, updateContextPluginToggleState } = useAIChatbot() as AIChatbotProviderValue;
    const showContextPlugin = useMemo(() => {
        return aiChatbotState?.contextPlugins?.isAllowedInSubscription;
    }, [aiChatbotState?.contextPlugins?.isAllowedInSubscription]);
    const isCursorContextEnabled = aiChatbotState?.contextPlugins?.cursorIntegration?.isEnabled;
    const isCopilotEnabled = aiChatbotState.isAiEnabled;

    return (
        <Card className="flex flex-col pt-0 pl-0 pr-0 mt-4">
            <div className="setup-guide-header flex items-center justify-between px-6">
                <div className="flex items-center gap-2">
                    <GearIcon fill="#455666" width="14" height="14" />
                    <H5>Set Up Context Plugins</H5>
                    {showContextPlugin && (
                        <Tooltip
                            message={
                                isCopilotEnabled
                                    ? 'Toggle context plugins visibility on the published portal.'
                                    : 'Enable API Copilot to use context plugins'
                            }
                            position="right"
                        >
                            <ToggleSwitch
                                disabled={!isCopilotEnabled}
                                onChange={(e) => updateContextPluginToggleState(e.target.checked)}
                                checked={!!isCursorContextEnabled}
                            />
                        </Tooltip>
                    )}
                </div>
                <Button
                    text=""
                    icon={<ChevronIcon className={collapsed ? 'rotate-180' : ''} />}
                    onClick={() => setCollapsed(!collapsed)}
                />
            </div>

            {!collapsed && (
                <div className="px-6 py-5">
                    
                    <H6 className="mb-4">
                        {STEPS.length} Steps to Enable Context Plugins
                    </H6>
                    <div className="flex flex-col mb-5">
                        {STEPS.map((step, index) => (
                            <div key={index} className="flex items-start gap-3 mb-4">
                                <div className="context-plugin-step-number flex items-center justify-center shrink-0">
                                    {index + 1}
                                </div>
                                <div className="min-w-0 flex-1">
                                    <p className="text-14 font-semibold text-charcoal-900 mb-1">
                                        {step.title}
                                    </p>
                                    <p className="text-13 text-goose-800">{step.description}</p>
                                </div>
                            </div>
                        ))}
                    </div>

                    <Button
                        text="Continue to SDK Publishing"
                        fill="secondary"
                        icon={<ExternalLinkIcon />}
                        onClick={() =>
                            window.open(packagePublishingUrl, '_blank', 'noopener,noreferrer')
                        }
                    />

                    <H6 className="mt-5">How It Works: Watch the Full Walkthrough</H6>
                    <div className="context-plugin-video-wrapper rounded-8 overflow-hidden">
                        <iframe
                            src={WALKTHROUGH_VIDEO_URL}
                            title="Publishing SDKs and enabling Context Plugins, step by step"
                            allowFullScreen
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            className="w-full context-plugin-video-frame"
                        />
                    </div>
                </div>
            )}
        </Card>
    );
};
